/**
 * errorClassLabel — maps raw `errorClass` bucket IDs to human-friendly labels
 * for the Operations / Error breakdown table, its mobile stacked view, and
 * the `ErrorBucketModal` drill-down title.
 *
 * Buckets come from the backend `errorClassExpr` CASE in
 * `src/services/analytics/queries.ts:3330-3370` — most are derived by
 * pattern-matching `raw_metadata->>'error'`, but `malformed_function_call`
 * and `child_observation_error` are written directly by
 * `src/dashboard/langfuse/sync.ts` (`deriveErrorFromObservations`) when the
 * trace has no inline error but a child generation observation does. New
 * bucket → add a row here; otherwise the table renders the raw snake_case
 * (and `titleCase` falls back gracefully).
 *
 * The raw bucket ID is still the database-key passed to
 * `useErrorBucketTraces(bucket, ...)` for the drill-down — this helper
 * affects DISPLAY only.
 *
 * Pattern mirrors `lib/trace-labels.ts` (kindLabel).
 */

const ERROR_CLASS_LABELS: Record<string, string> = {
  // Bot LLM-call failures (Vertex/Gemini)
  llm_timeout: 'LLM timeout',
  llm_exhausted: 'LLM retries exhausted',
  llm_safety: 'Safety filter blocked',
  malformed_function_call: 'Malformed tool call (Gemini)',
  rate_limit: 'Rate limit (429)',
  upstream_unavailable: 'Upstream unavailable (5xx)',

  // SalesMind integration
  salesmind_unavailable: 'SalesMind unavailable',
  salesmind_auth: 'SalesMind auth',
  salesmind_circuit_open: 'SalesMind circuit open',

  // Tool dispatch
  tool_failed: 'Tool failed',
  tool_not_found: 'Tool not found',

  // Knowledge base
  kb_unavailable: 'Knowledge base unavailable',

  // Access control
  access_denied: 'Access denied',
  not_rm: 'Not an RM',

  // Observation-derived fallback when a child observation is ERROR but
  // its statusMessage doesn't match any specific bucket above. Surfaces
  // a generic label rather than `other` so operators know it came from
  // the silent-failure backfill rather than an inline error string.
  child_observation_error: 'Child observation error',

  // PascalCase JS error class names written by `message-processor.ts:1762`
  // (`logErrorClass = err.name`) — `errorClassExpr` passes these through
  // verbatim because the CASE pattern-match only fires when `errorClass`
  // is null/empty. Naming them explicitly here makes the dashboard read
  // like English instead of the camelCase auto-split fallback below.
  Error: 'Error (generic)',
  VertexExhaustedError: 'LLM retries exhausted',
  VertexTimeoutError: 'LLM timeout',
  // NOT a timeout and NOT a provider failure: the caller stopped wanting the answer
  // (`/stop`, the wall-clock cap, a budget that fired). `vertex/types.ts` gives it its
  // own class for exactly this reason — folding a cancel into the timeout bucket makes
  // every abandoned call look like an incident. Unlabelled, it reached the table as
  // `Vertex Cancelled Error` from the PascalCase fallback, i.e. as a fault.
  VertexCancelledError: 'Cancelled by caller',
  LLMTimeoutError: 'LLM timeout',
  LLMExhaustedError: 'LLM retries exhausted',
  LLMSafetyError: 'Safety filter blocked',
  SalesMindUnavailableError: 'SalesMind unavailable',
  SalesMindAuthError: 'SalesMind auth',
  SalesMindCircuitOpenError: 'SalesMind circuit open',
  RateLimitError: 'Rate limit (429)',
  ToolExecutionError: 'Tool failed',
  ToolNotFoundError: 'Tool not found',
  KBUnavailableError: 'Knowledge base unavailable',
  AccessDeniedError: 'Access denied',
  NotRMError: 'Not an RM',

  // Catch-all
  other: 'Other',
};

/**
 * Title-case fallback for unknown buckets. Handles two shapes the backend
 * actually emits:
 *
 *   1. snake_case / kebab-case / colon-separated (`some_future_class`) —
 *      from `errorClassExpr` CASE arms in queries.ts.
 *   2. PascalCase JS error class names (`VertexExhaustedError`) — passed
 *      through verbatim from `raw_metadata->>'errorClass'` when the bot
 *      wrote `err.name` of an unmapped error class.
 *
 * The algorithm: first insert spaces at PascalCase boundaries (split
 * `Xx` apart and split `XXxx` so `RM` stays attached to the next word),
 * then split on `-`/`_`/`:` separators, capitalise the first letter of
 * each segment, leave the rest of each segment as-is so acronyms in
 * `VertexAI` survive. Keeps the table readable when a new bucket lands
 * in production before this map is updated.
 */
function titleCase(raw: string): string {
  if (!raw) return 'Unknown';
  // Insert a separator before each PascalCase boundary. Two passes so
  // `LLMTimeoutError` becomes `LLM_Timeout_Error` rather than
  // `L_L_M_Timeout_Error` — first split acronym-to-Word, then
  // word-to-Word.
  const withBoundaries = raw
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1_$2') // LLMTimeout → LLM_Timeout
    .replace(/([a-z\d])([A-Z])/g, '$1_$2');    // somethingError → something_Error
  return withBoundaries
    .split(/[-_:]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

export function errorClassLabel(bucket: string | null | undefined): string {
  if (!bucket) return 'Unknown';
  return ERROR_CLASS_LABELS[bucket] ?? titleCase(bucket);
}
